import { useEffect } from 'react';
import { useUIStore } from '@/store/uiStore';
import { useAuthStore } from '@/store/authStore';
import { initTelegram, showBackButton, hideBackButton, applyTheme } from '@/lib/telegram';
import { BottomNav } from './BottomNav';
import { LoadingOverlay } from './LoadingOverlay';
import { Notifications } from './Notifications';
import { LoadingScreen } from '@/screens/LoadingScreen';
import { DebugLoginScreen } from '@/screens/DebugLoginScreen';
import { CharacterCreationScreen } from '@/screens/CharacterCreationScreen';
import { BackstoryScreen } from '@/screens/BackstoryScreen';
import { MainScreen } from '@/screens/MainScreen';
import { WaifusScreen } from '@/screens/WaifusScreen';
import { JournalScreen } from '@/screens/JournalScreen';
import { PlayerMenuScreen } from '@/screens/PlayerMenuScreen';
import { AchievementsScreen } from '@/screens/AchievementsScreen';
import { SettingsScreen } from '@/screens/SettingsScreen';
import { ModalRoot } from '@/components/modals/ModalRoot';
import { usePlayer } from '@/api/queries';
import { useInitFlow } from '@/lib/useInitFlow';

const SCREENS_WITHOUT_NAV = new Set([
  'loading',
  'debug-login',
  'character-creation',
  'backstory',
]);

const SCREENS_WITH_BACK = new Set([
  'waifus',
  'journal',
  'player-menu',
  'achievements',
  'settings',
]);

export function AppShell() {
  const currentScreen = useUIStore((s) => s.currentScreen);
  const setScreen = useUIStore((s) => s.setScreen);
  const userId = useAuthStore((s) => s.userId);

  // Initialize Telegram SDK on mount
  useEffect(() => {
    initTelegram();
    applyTheme();
  }, []);

  useInitFlow();

  // Track player data so we can refetch on screen switch
  usePlayer(userId);

  // Bind Telegram BackButton to non-main screens
  useEffect(() => {
    if (SCREENS_WITH_BACK.has(currentScreen)) {
      const cleanup = showBackButton(() => setScreen('main'));
      return cleanup;
    }
    hideBackButton();
    return undefined;
  }, [currentScreen, setScreen]);

  const showNav = !SCREENS_WITHOUT_NAV.has(currentScreen);

  return (
    <div className="flex flex-col h-full w-full relative">
      <Notifications />
      <main className="flex-1 min-h-0 flex flex-col relative overflow-hidden">
        {renderScreen(currentScreen)}
      </main>
      {showNav && <BottomNav />}
      <ModalRoot />
      <LoadingOverlay />
    </div>
  );
}

function renderScreen(screen: string) {
  switch (screen) {
    case 'loading':
      return <LoadingScreen />;
    case 'debug-login':
      return <DebugLoginScreen />;
    case 'character-creation':
      return <CharacterCreationScreen />;
    case 'backstory':
      return <BackstoryScreen />;
    case 'main':
      return <MainScreen />;
    case 'waifus':
      return <WaifusScreen />;
    case 'journal':
      return <JournalScreen />;
    case 'player-menu':
      return <PlayerMenuScreen />;
    case 'achievements':
      return <AchievementsScreen />;
    case 'settings':
      return <SettingsScreen />;
    default:
      return <LoadingScreen />;
  }
}
